import React, { useLayoutEffect, useRef, useState } from 'react';
import useLinkedScrollLeft from '../../hooks/useLinkedScrollLeft';
import useDragScroll from '../../hooks/useDragScroll';
import StickyScrollbar from '../ui/StickyScrollbar';
import {
  PAGE_SCROLL_PANE_X,
  STICKY_REGION_CONTAINMENT,
  STICKY_SECTION_HEADER_Z,
} from '../../constants/stickyTable';

/**
 * THE BUDGET → CREW MEMBERS TABLE'S FRAME: one page scroll, a pinned header,
 * a pinned TOTAL row, a sticky horizontal scrollbar (2026-09-26).
 *
 * Owner, after the order tables moved off their inner scroll box: *"yh this
 * worked well do all other similar pages"* — scope chosen: the Breakdown and
 * this table. Until then the crew table sat in an `overflow-x-auto
 * overflow-y-auto max-h-[700px]` box — a scroll window inside the scrolling
 * page, the arrangement he ruled out (*"a scroll within a scroll? not nice UI
 * desing"*). This is 🅱️ of `constants/stickyTable.js`, the model the order
 * tables and the Breakdown use; the money rows it frames are the PAGE's and
 * are passed in as `children` untouched.
 *
 * ── The panes ───────────────────────────────────────────────────────
 *   1. HEADER PANE — `sticky` at `stickyTop` against the page (the app bar;
 *      this page has no sticky toolbar), a header-only `<table>` in its own
 *      x-scroller, opaque fill.
 *   2. ROWS PANE — x-only (`PAGE_SCROLL_PANE_X`: `overflow-y: hidden`, no
 *      height cap), scrolling with the page. Its `<table>` is the ONE WIDTH
 *      SOURCE (below).
 *   3. FOOTER BLOCK — `sticky` at the bottom: the TOTAL row in its own
 *      x-scroller, then the shared `ui/StickyScrollbar` while the table
 *      overflows. It replaces the old `sticky bottom-0` total `<td>`s, which
 *      pinned inside the 700px box; the owner's crew total stays in view the
 *      same way, now against the page. At the table's end it rests in flow,
 *      below the last row (it cannot cover a row it has reached).
 *      `stickyBottom` is the MEASURED clearance above the page's fixed save
 *      bar (its fade's top edge — the same occluder `utils/saveBarSpacer.js`
 *      measures); `null` falls back to the mobile-nav / desktop classes.
 * All four scrollLefts are linked two-way by `hooks/useLinkedScrollLeft`.
 *
 * ── 🔑 THE ONE WIDTH SOURCE, and why it is MEASURED ──────────────────
 * The md+ table is `table-auto`: its columns size to their CONTENT (names with
 * badges, money, dates), and there is no declared width to share the way the
 * order tables and the Breakdown share a `<colgroup>`. A second, separate
 * auto-layout header table would size to its HEADINGS alone and could never
 * line up. So:
 *   - the ROWS table also carries a copy of the header row and of the total
 *     row, both `visibility: collapse` — they take no height and are not in
 *     the accessibility tree, but auto layout still sizes every column for
 *     them, exactly as the old single table sized its columns for its
 *     `<thead>` and its total row;
 *   - its collapsed header copy's cells are MEASURED (a `ResizeObserver` on the
 *     table, the pane and every measured cell), and those pixel widths are
 *     applied to the header and total tables, which are `table-fixed` at the
 *     rows table's measured width.
 * Nothing is truncated that was not before: the rows table is laid out exactly
 * as the old single table was (same class, same cells, same header and total
 * content participating), and the other two tables copy its widths. Fixed
 * declared widths were rejected: they would have to be chosen per viewport
 * for content that varies per production, and a crew name or a total that
 * outgrew its column would be clipped — a clipped money figure is a wrong
 * money figure (`components/budget/CLAUDE.md`).
 *
 * Before the first measurement (and always in jsdom, which lays nothing out
 * and measures 0) the header and total tables render with the rows table's
 * own classes and no widths — present and correct in content, unaligned for
 * one frame in a browser.
 *
 * ♿ Three `<table>`s re-joined as one: the wrapper is `role="table"`, the
 * header and total tables `role="none"` with explicit `rowgroup`/`row`/
 * `columnheader`/`cell` roles, the rows table `role="rowgroup"`; the collapsed
 * copies are also `aria-hidden`.
 *
 * ⚠️ jsdom has no stylesheet or layout: tests pin the structure and the wiring.
 * Pinning, alignment and the footer's clearance are BROWSER checks.
 *
 * @param {Array<{key: string, className: string, label: React.ReactNode}>} headerCells
 * @param {Array<{key: string, className: string, content?: React.ReactNode, testId?: string}>} footerCells
 */
const COLLAPSED = { visibility: 'collapse' };
const round2 = (x) => Math.round(x * 100) / 100;

function sameGeometry(a, b) {
  return a.tableWidth === b.tableWidth
    && a.paneWidth === b.paneWidth
    && a.widths.length === b.widths.length
    && a.widths.every((w, i) => w === b.widths[i]);
}

export default function CrewMembersTableFrame({
  headerCells,
  footerCells,
  footerRowTestId,
  bodyTableClassName,
  stickyTop = 0,
  stickyBottom = null,
  className = '',
  freezeWidths = false,
  children,
}) {
  const headRef = useRef(null);
  const rowsRef = useRef(null);
  const footRef = useRef(null);
  const barRef = useRef(null);
  const bodyTableRef = useRef(null);
  const measureRowRef = useRef(null);
  const [geom, setGeom] = useState(null);

  useLayoutEffect(() => {
    const table = bodyTableRef.current;
    const pane = rowsRef.current;
    const row = measureRowRef.current;
    if (!table || !pane || !row) return undefined;
    const measure = () => {
      const tableWidth = round2(table.getBoundingClientRect().width);
      // 0 = nothing to measure (a `display: none` ancestor — the Gantt view on
      // desktop — or jsdom), never a width: keep the unmeasured rendering.
      if (!(tableWidth > 0)) { setGeom((g) => (g === null ? g : null)); return; }
      const next = {
        tableWidth,
        paneWidth: pane.clientWidth,
        widths: [...row.children].map((c) => round2(c.getBoundingClientRect().width)),
      };
      setGeom((g) => (g && sameGeometry(g, next) ? g : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(table);
    ro.observe(pane);
    [...row.children].forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [headerCells.length]);

  useLinkedScrollLeft([rowsRef, headRef, footRef, barRef]);
  // 🖱️ Click-and-drag panning (2026-09-27): the pane under the press pans
  // sideways (the link moves the rest), the PAGE vertically. The page marks
  // the expanded row editor `data-pan-skip` (`hooks/useDragScroll`).
  const panBoxRef = useRef(null);
  useDragScroll(panBoxRef, { axis: 'both', verticalPansWindow: true });

  // The geometry the rows table is frozen to while `freezeWidths` holds: the
  // FIRST measurement taken while frozen is the pre-controls one, and it is
  // kept until the freeze lifts (a later measurement of the frozen table
  // could only echo it, but nothing has to rely on that).
  const frozenRef = useRef(null);
  if (!freezeWidths) frozenRef.current = null;
  else if (frozenRef.current == null && geom) frozenRef.current = geom;
  const frozen = freezeWidths ? frozenRef.current : null;
  const overflowsX = geom != null && geom.tableWidth > geom.paneWidth + 0.5;
  const fixedTableClass = geom ? 'text-sm table-fixed' : bodyTableClassName;
  const fixedTableStyle = geom ? { width: geom.tableWidth } : undefined;
  const widthOf = (i) => (geom && geom.widths[i] > 0 ? { width: geom.widths[i] } : undefined);

  const footerBlockPosition = stickyBottom == null ? 'bottom-16 lg:bottom-0' : '';
  const footerBlockStyle = stickyBottom == null ? undefined : { bottom: stickyBottom };

  return (
    <div
      ref={panBoxRef}
      role="table"
      // Not a scroll container: `overflow: clip` clips the rounded corners
      // without becoming one, so the panes' `sticky` resolves against the PAGE.
      // `relative` so no absolutely-positioned descendant resolves against the
      // initial containing block (`STICKY_REGION_CONTAINMENT`'s docblock).
      className={`${STICKY_REGION_CONTAINMENT} overflow-clip border border-gray-200 dark:border-gray-700 rounded-lg ${className}`}
      data-testid="crew-table-view"
    >
      <div
        ref={headRef}
        className={`sticky ${STICKY_SECTION_HEADER_Z} ${PAGE_SCROLL_PANE_X} bg-white dark:bg-gray-800`}
        style={{ top: stickyTop }}
        data-page-scroll-pane=""
        data-testid="crew-table-header"
      >
        <table role="none" className={fixedTableClass} style={fixedTableStyle}>
          <thead role="rowgroup">
            <tr role="row" className="border-b border-gray-200 dark:border-gray-700">
              {headerCells.map((c, i) => (
                <th key={c.key} role="columnheader" className={c.className} style={widthOf(i)}>{c.label}</th>
              ))}
            </tr>
          </thead>
        </table>
      </div>

      <div
        ref={rowsRef}
        className={`${STICKY_REGION_CONTAINMENT} ${PAGE_SCROLL_PANE_X}`}
        data-page-scroll-pane=""
        data-testid="crew-table-rows"
      >
        {/* 🔀 `freezeWidths` (2026-10-25, in-place row editing — the Breakdown's
            model): while a row's own cells are controls, an AUTO-layout table
            would size the columns to those controls and the row would change
            shape. Frozen, the rows table takes `table-fixed` + a `<colgroup>` of
            the widths measured BEFORE the controls arrived (the last `geom`), so
            nothing moves; on unfreeze it is auto-layout again, exactly as before.
            Unmeasured (jsdom, first paint) → no freeze, never a guessed width. */}
        <table
          ref={bodyTableRef}
          role="rowgroup"
          className={frozen ? 'text-sm table-fixed' : bodyTableClassName}
          style={frozen ? { width: frozen.tableWidth } : undefined}
          data-frozen-widths={frozen ? '' : undefined}
        >
          {frozen && (
            <colgroup>
              {frozen.widths.map((w, i) => <col key={headerCells[i] ? headerCells[i].key : i} style={{ width: w }} />)}
            </colgroup>
          )}
          {/* The collapsed copies — the width source's inputs. See the docblock. */}
          <thead aria-hidden="true">
            <tr ref={measureRowRef} style={COLLAPSED} data-crew-measure-row="">
              {headerCells.map((c) => <th key={c.key} className={c.className}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>{children}</tbody>
          <tfoot aria-hidden="true">
            <tr style={COLLAPSED} className="font-semibold">
              {footerCells.map((c) => <td key={c.key} className={c.className}>{c.content}</td>)}
            </tr>
          </tfoot>
        </table>
      </div>

      <div
        className={`sticky ${STICKY_SECTION_HEADER_Z} ${footerBlockPosition}`}
        style={footerBlockStyle}
        data-testid="crew-table-footer-block"
      >
        <div
          ref={footRef}
          className={`${PAGE_SCROLL_PANE_X} bg-white dark:bg-gray-800`}
          data-page-scroll-pane=""
          data-testid="crew-table-footer"
        >
          <table role="none" className={fixedTableClass} style={fixedTableStyle}>
            <tbody role="rowgroup">
              <tr role="row" className="font-semibold" data-testid={footerRowTestId}>
                {footerCells.map((c, i) => (
                  <td key={c.key} role="cell" className={c.className} style={widthOf(i)} data-testid={c.testId}>{c.content}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        {overflowsX ? (
          <StickyScrollbar
            ref={barRef}
            stick={false}
            contentStyle={{ minWidth: geom.tableWidth }}
            testId="crew-table-scrollbar"
          />
        ) : null}
      </div>
    </div>
  );
}
